import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '@monksflow/monks-ui';
import { Fingerprint, Pencil, Play } from 'lucide-react';
import {
  useGenerateTrackingCode,
  type TaxonomyField,
  type TaxonomySettings,
  DEFAULT_TRACKING_CODE_CAP,
  NEAR_CAP_FRACTION,
} from '@/entities/taxonomy';
import { PLATFORM_CHAR_LIMITS } from '../../lib/channelLimits';
import {
  DEFAULT_FIELD_DELIMITER,
  DEFAULT_SUBFIELD_DELIMITER,
} from '@inference.engine/taxonomy/web';
import { patternToString } from '../../lib/trackingPattern';
import { TrackingCodeComposer } from '../TrackingCodeComposer/TrackingCodeComposer';

export interface SchemaSettingsProps {
  settings: TaxonomySettings;
  platforms: string[];
  /** All schema fields — the tracking-code pattern can reference them. */
  fields: TaxonomyField[];
  taxonomyId: string;
  versionId: string;
  readOnly: boolean;
  /**
   * Whether the viewer may mint a sample code. Separate from `readOnly`, which
   * also closes on a published or historical version: minting is gated by the
   * manage permission alone, so someone who may edit somewhere can still preview
   * a code for a version they are only reading.
   */
  canMint: boolean;
  /** Codes minted so far. Absent when the caller did not measure it. */
  trackingCodeCount?: number;
  onChange: (settings: TaxonomySettings) => void;
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </span>
  );
}

/**
 * Output-formatting settings for the schema — how the generated name string is
 * assembled. Lives under the Rules tab (alongside conditional rules) to keep the
 * main builder area lean: delimiters and per-channel character limits. All
 * persist on the version's `settings` jsonb.
 */
export function SchemaSettings({
  settings,
  platforms,
  fields,
  taxonomyId,
  versionId,
  readOnly,
  canMint,
  trackingCodeCount,
  onChange,
}: SchemaSettingsProps) {
  const { t } = useTranslation('knowledge');
  const [composerOpen, setComposerOpen] = useState(false);
  const [sample, setSample] = useState<string | null>(null);
  const generate = useGenerateTrackingCode(taxonomyId);

  const trackingPattern = settings.trackingCode?.pattern ?? [];
  /**
   * The same default the server enforces. Duplicated rather than fetched because
   * the alternative is a round trip to learn a constant — but it means the two
   * have to move together, which the constant's name is doing the work of saying.
   */
  const cap = settings.codeCap ?? DEFAULT_TRACKING_CODE_CAP;
  const trackingLabel = trackingPattern.length ? patternToString(trackingPattern, fields) : '';

  const handleGenerateSample = () => {
    const refIds = [
      ...new Set(
        trackingPattern
          .filter((p) => p.kind === 'field' && p.fieldId)
          .map((p) => p.fieldId as string),
      ),
    ];
    const values = refIds.map((fieldId) => ({
      fieldId,
      value: fields.find((f) => f.id === fieldId)?.values[0]?.value ?? '',
    }));
    generate.mutate({ versionId, values }, { onSuccess: (res) => setSample(res.code) });
  };

  const limitValue = (platform: string): string => {
    const override = settings.channelLimits?.find((l) => l.platform === platform);
    if (override) return String(override.limit);
    const def = PLATFORM_CHAR_LIMITS[platform];
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- PLATFORM_CHAR_LIMITS is indexed by an arbitrary platform name
    return def != null ? String(def) : '';
  };

  const setLimit = (platform: string, raw: string) => {
    const others = (settings.channelLimits ?? []).filter((l) => l.platform !== platform);
    const n = Number.parseInt(raw, 10);
    // `< 1` joins blank and NaN as "not a usable number", rather than being a
    // second rule: the API enforces `@Min(1)`, so writing a 0 through made every
    // autosave 400 until the field was retyped. Dropping the entry means no
    // limit for that platform, which is what an empty box already means.
    const next =
      raw.trim() === '' || Number.isNaN(n) || n < 1 ? others : [...others, { platform, limit: n }];
    onChange({ ...settings, channelLimits: next });
  };

  return (
    <div className="flex flex-col gap-5 rounded-xl border border-border-light bg-card p-5">
      <div>
        <h3 className="text-sm font-semibold text-foreground">
          {t('taxonomy.schemaSettings.formattingOutput')}
        </h3>
        <p className="text-xs text-muted-foreground">
          {t('taxonomy.schemaSettings.howGeneratedNameStringAssembled')}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {/* Delimiters */}
        <div className="flex flex-col gap-1.5">
          <Label>{t('taxonomy.schemaSettings.fieldSeparator')}</Label>
          <Input
            value={settings.fieldDelimiter ?? DEFAULT_FIELD_DELIMITER}
            disabled={readOnly}
            maxLength={5}
            onChange={(e) => onChange({ ...settings, fieldDelimiter: e.target.value })}
            className="w-24 bg-input text-center font-mono"
          />
          <span className="text-[11px] text-muted-foreground">
            {t('taxonomy.schemaSettings.betweenTopLevelFields')}
          </span>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>{t('taxonomy.schemaSettings.subfieldSeparator')}</Label>
          <Input
            value={settings.subfieldDelimiter ?? DEFAULT_SUBFIELD_DELIMITER}
            disabled={readOnly}
            maxLength={5}
            onChange={(e) => onChange({ ...settings, subfieldDelimiter: e.target.value })}
            className="w-24 bg-input text-center font-mono"
          />
          <span className="text-[11px] text-muted-foreground">
            {t('taxonomy.schemaSettings.betweenFieldSubfields')}
          </span>
        </div>
      </div>

      {/* Channel character limits */}
      <div className="flex flex-col gap-1.5 border-t border-border-light pt-4">
        <Label>{t('taxonomy.schemaSettings.channelCharacterLimits')}</Label>
        {platforms.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">
            {t('taxonomy.schemaSettings.selectPlatformsSchemaPropertiesSet')}
          </p>
        ) : (
          <div className="mt-1 flex flex-wrap gap-3">
            {platforms.map((platform) => {
              const isOverride = !!settings.channelLimits?.some((l) => l.platform === platform);
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- PLATFORM_CHAR_LIMITS is indexed by an arbitrary platform name
              const hasDefault = PLATFORM_CHAR_LIMITS[platform] != null;
              return (
                <div key={platform} className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-foreground">{platform}</span>
                  <Input
                    type="number"
                    min={1}
                    value={limitValue(platform)}
                    disabled={readOnly}
                    placeholder={t('taxonomy.schemaSettings.none')}
                    onChange={(e) => setLimit(platform, e.target.value)}
                    className="h-8 w-24 bg-input"
                  />
                  <span className="text-[10px] text-muted-foreground">
                    {isOverride
                      ? t('taxonomy.schemaSettings.custom')
                      : hasDefault
                        ? t('taxonomy.schemaSettings.default')
                        : t('taxonomy.schemaSettings.noDefault')}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Unique tracking code — a SEPARATE join key (not part of the name). */}
      <div className="flex flex-col gap-2 border-t border-border-light pt-4">
        <div className="flex items-center gap-2">
          <Fingerprint className="size-4 text-muted-foreground" aria-hidden />
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('taxonomy.schemaSettings.uniqueTrackingCode')}
          </span>
        </div>
        <p className="text-[11px] text-muted-foreground">
          {t('taxonomy.schemaSettings.standaloneUniqueIdGeneratedTaxonomy')}
        </p>
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border-light bg-surface/40 px-3 py-2">
          <span className="min-w-0 flex-1 truncate font-mono text-xs text-foreground">
            {trackingLabel || (
              <span className="text-muted-foreground/60">
                {t('taxonomy.schemaSettings.notConfigured')}
              </span>
            )}
          </span>
          {sample && (
            <span className="rounded bg-emerald-50 px-2 py-0.5 font-mono text-xs text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
              {sample}
            </span>
          )}
          {(canMint || !readOnly) && (
            <div className="flex items-center gap-1.5">
              {/* Minting allocates a real, permanent tracking code, so the endpoint
                  requires manage: left ungated this button could only ever 403. */}
              {canMint && (
                <Button
                  variant="outline"
                  size="mini"
                  disabled={trackingPattern.length === 0 || generate.isPending}
                  onClick={handleGenerateSample}
                >
                  <Play className="size-3.5" />
                  {generate.isPending
                    ? t('taxonomy.schemaSettings.generating')
                    : t('taxonomy.schemaSettings.sample')}
                </Button>
              )}
              {!readOnly && (
                <Button variant="outline" size="mini" onClick={() => setComposerOpen(true)}>
                  <Pencil className="size-3.5" />
                  {t('taxonomy.schemaSettings.configure')}
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Only once it is close. A budget line on a schema that has minted three
            codes is noise, and noise is what teaches people to stop reading. */}
        {trackingCodeCount !== undefined && trackingCodeCount >= cap * NEAR_CAP_FRACTION && (
          <p role="status" className="mt-2 text-xs text-amber-700 dark:text-amber-400">
            {t('taxonomy.schemaSettings.trackingCodesUsed', {
              used: trackingCodeCount,
              cap,
            })}{' '}
            {t('taxonomy.schemaSettings.trackingCodesNearlyGone')}
          </p>
        )}
      </div>

      {composerOpen && (
        <TrackingCodeComposer
          open
          nameless
          mintedCount={trackingCodeCount}
          title={t('taxonomy.schemaSettings.configureTrackingCode')}
          field={{
            id: '__tracking_code__',
            name: t('taxonomy.schemaSettings.trackingCode'),
            group: 'media',
            type: 'computed',
            required: false,
            pattern: trackingPattern,
            values: [],
          }}
          fields={fields}
          onClose={() => setComposerOpen(false)}
          onSave={({ pattern }) => {
            onChange({ ...settings, trackingCode: { pattern } });
            // The chip shows a code minted from the pattern that just went away,
            // so it would keep advertising a format the schema no longer has.
            setSample(null);
            setComposerOpen(false);
          }}
        />
      )}
    </div>
  );
}

SchemaSettings.displayName = 'SchemaSettings';
